/*
  NOAN — comportamiento de las secciones c-*.
  Custom elements sin dependencias:
    <c-rail-nav>             flechas + barra de progreso para un riel con scroll horizontal
    <c-collections-showcase> tarjetas que se expanden (acordeón) con rotación automática
    <c-tabs>                 pestañas que muestran un panel a la vez
*/

(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ===== Riel con flechas y progreso ===== */
  class CRailNav extends HTMLElement {
    connectedCallback() {
      const scope = this.closest('[data-rail-scope]') || this.parentElement;
      this.scope = scope;
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.onPrev = () => this.step(-1);
      this.onNext = () => this.step(1);
      this.onScroll = () => this.update();
      if (this.prev) this.prev.addEventListener('click', this.onPrev);
      if (this.next) this.next.addEventListener('click', this.onNext);
      this.bind();
      scope.addEventListener('c:panel-change', () => this.bind());
    }

    disconnectedCallback() {
      if (this.rail) this.rail.removeEventListener('scroll', this.onScroll);
    }

    bind() {
      if (this.rail) this.rail.removeEventListener('scroll', this.onScroll);
      this.rail = [...this.scope.querySelectorAll('[data-rail]')].find((el) => !el.closest('[hidden]'));
      if (!this.rail) return;
      const panel = this.rail.closest('[role="tabpanel"]') || this.scope;
      this.bar = panel.querySelector('[data-rail-progress]') || this.scope.querySelector('[data-rail-progress]');
      this.dots = panel.querySelector('[data-rail-dots]') || this.scope.querySelector('[data-rail-dots]');
      this.rail.addEventListener('scroll', this.onScroll, { passive: true });
      if (!this.onResize) {
        this.onResize = () => { this.buildDots(); this.update(); };
        window.addEventListener('resize', this.onResize);
      }
      this.buildDots();
      this.update();
    }

    /* Una "página" = lo que cabe en pantalla. Activo = pastilla; inactivo = círculo. */
    buildDots() {
      if (!this.dots || !this.rail) return;
      const pages = Math.max(1, Math.ceil(this.rail.scrollWidth / this.rail.clientWidth - 0.05));
      this.pages = pages;
      this.dots.hidden = pages < 2;
      if (this.dots.children.length === pages) return;
      this.dots.replaceChildren(
        ...Array.from({ length: pages }, (_, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'c-dots__dot';
          b.setAttribute('aria-label', `Ver grupo ${i + 1} de ${pages}`);
          b.addEventListener('click', () => {
            const max = this.rail.scrollWidth - this.rail.clientWidth;
            this.rail.scrollTo({ left: pages > 1 ? (max * i) / (pages - 1) : 0, behavior: reduceMotion ? 'auto' : 'smooth' });
          });
          return b;
        })
      );
    }

    step(dir) {
      if (!this.rail) return;
      const card = this.rail.querySelector(':scope > *:nth-child(2)') || this.rail.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
      const amount = card ? card.getBoundingClientRect().width + gap : this.rail.clientWidth * 0.8;
      this.rail.scrollBy({ left: dir * amount, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    update() {
      if (!this.rail) return;
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      const pos = this.rail.scrollLeft;
      if (this.prev) this.prev.disabled = pos <= 2;
      if (this.next) this.next.disabled = pos >= max - 2;
      if (this.dots && this.pages > 1) {
        const active = max > 0 ? Math.round((pos / max) * (this.pages - 1)) : 0;
        [...this.dots.children].forEach((d, i) => d.setAttribute('aria-current', i === active ? 'true' : 'false'));
      }
      if (this.bar) {
        const visible = this.rail.clientWidth / this.rail.scrollWidth;
        const ratio = max > 0 ? visible + (1 - visible) * (pos / max) : 1;
        this.bar.style.width = `${Math.round(ratio * 100)}%`;
      }
    }
  }

  /* ===== Colecciones en acordeón ===== */
  class CCollectionsShowcase extends HTMLElement {
    connectedCallback() {
      this.cards = [...this.querySelectorAll('[data-card]')];
      this.bars = [...this.querySelectorAll('[data-bar]')];
      this.counter = this.querySelector('[data-counter]');
      this.interval = parseInt(this.dataset.interval || '0', 10) * 1000;
      this.index = 0;
      // Hover: expande la tarjeta solo si el mouse se movió de verdad. Al expandirse, las
      // tarjetas cambian de ancho y se corren debajo del puntero quieto; sin este control
      // eso dispara mouseenter en la vecina y entra en un vaivén.
      const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.lastMove = 0;
      this.lastChange = 0;
      this.lockUntil = 0;
      this.addEventListener('mousemove', (e) => {
        this.lastMove = Date.now();
        this.pointer = { x: e.clientX, y: e.clientY };
        this.scheduleHover();
      });
      this.cards.forEach((card, i) => {
        const trigger = card.querySelector('[data-card-trigger]');
        if (trigger) trigger.addEventListener('click', () => this.go(i, true));
      });
      this.scheduleHover = () => {
        if (!canHover.matches) return;
        clearTimeout(this.hoverTimer);
        const wait = Math.max(150, this.lockUntil - Date.now());
        this.hoverTimer = setTimeout(() => {
          // El desplazamiento de las tarjetas no genera mousemove: si no hubo movimiento
          // real desde el último cambio, no se hace nada.
          if (!this.pointer || this.lastMove <= this.lastChange) return;
          const el = document.elementFromPoint(this.pointer.x, this.pointer.y);
          const card = el && el.closest('[data-card]');
          const i = this.cards.indexOf(card);
          if (i >= 0 && i !== this.index) {
            this.go(i, false);
            this.lastChange = Date.now();
            this.lockUntil = this.lastChange + 650;
          }
        }, wait);
      };
      const prev = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1, true));
      if (next) next.addEventListener('click', () => this.go(this.index + 1, true));
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.go(0, false);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    start() {
      this.stop();
      if (!this.interval || reduceMotion || this.cards.length < 2) return;
      this.timer = setInterval(() => this.go(this.index + 1, false), this.interval);
    }

    stop() {
      clearInterval(this.timer);
    }

    go(i, fromUser) {
      const total = this.cards.length;
      if (!total) return;
      this.index = (i + total) % total;
      this.cards.forEach((card, n) => {
        const open = n === this.index;
        card.classList.toggle('is-open', open);
        const trigger = card.querySelector('[data-card-trigger]');
        if (trigger) trigger.setAttribute('aria-pressed', open ? 'true' : 'false');
        const content = card.querySelector('[data-card-content]');
        if (content) content.toggleAttribute('inert', !open);
      });
      this.bars.forEach((bar, n) => bar.classList.toggle('is-active', n === this.index));
      if (this.counter) this.counter.textContent = String(this.index + 1).padStart(2, '0');
      if (fromUser) {
        const rail = this.querySelector('[data-showcase-rail]');
        const card = this.cards[this.index];
        if (rail && card && rail.scrollWidth > rail.clientWidth) {
          rail.scrollTo({ left: card.offsetLeft - rail.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
        }
        this.start();
      }
    }
  }

  /* ===== Pestañas ===== */
  class CTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.word = this.querySelector('[data-tab-word]');
      this.counter = this.querySelector('[data-tab-counter]');
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        });
      });
      this.querySelectorAll('[data-tab-step]').forEach((btn) => {
        btn.addEventListener('click', () => this.select(this.current + Number(btn.dataset.tabStep)));
      });
      this.current = Math.max(0, this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'));
    }

    select(i) {
      const total = this.tabs.length;
      if (!total) return;
      this.current = (i + total) % total;
      this.tabs.forEach((tab, n) => {
        const on = n === this.current;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
      this.panels.forEach((panel, n) => {
        panel.hidden = n !== this.current;
      });
      const label = this.tabs[this.current].dataset.label || '';
      if (this.word) this.word.textContent = label;
      if (this.counter) this.counter.textContent = `${String(this.current + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
      const rail = this.panels[this.current] && this.panels[this.current].querySelector('[data-rail]');
      if (rail) rail.scrollLeft = 0;
      this.dispatchEvent(new CustomEvent('c:panel-change', { bubbles: true }));
    }
  }

  /* ===== Galería de producto: riel con scroll-snap, flechas, contador y miniaturas ===== */
  class CGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.counter = this.querySelector('[data-gallery-counter]');
      if (!this.track || !this.slides.length) return;
      this.index = 0;
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i)));

      // Carrusel de miniaturas (una sola fila)
      this.thumbRail = this.querySelector('[data-thumbs]');
      this.thumbWrap = this.querySelector('[data-thumbs-wrap]');
      this.thumbPrev = this.querySelector('[data-thumbs-prev]');
      this.thumbNext = this.querySelector('[data-thumbs-next]');
      if (this.thumbRail) {
        const page = (dir) => this.thumbRail.scrollBy({ left: dir * this.thumbRail.clientWidth * 0.8, behavior: reduceMotion ? 'auto' : 'smooth' });
        this.thumbPrev?.addEventListener('click', () => page(-1));
        this.thumbNext?.addEventListener('click', () => page(1));
        this.thumbRail.addEventListener('scroll', () => this.updateThumbNav(), { passive: true });
        window.addEventListener('resize', () => this.updateThumbNav());
        this.updateThumbNav();
      }
      let raf;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          if (i !== this.index) this.mark(i);
        });
      }, { passive: true });
      this.mark(0);
      this.setupLightbox();
    }

    /* ----- Vista ampliada: todas las fotos, navegación y zoom con el mouse ----- */
    setupLightbox() {
      this.lb = this.querySelector('[data-lightbox]');
      if (!this.lb) return;
      this.lbTrack = this.lb.querySelector('[data-lightbox-track]');
      this.lbSlides = [...this.lb.querySelectorAll('[data-lightbox-slide]')];
      this.lbThumbs = [...this.lb.querySelectorAll('[data-lightbox-thumb]')];
      this.lbCounter = this.lb.querySelector('[data-lightbox-counter]');
      this.lbIndex = 0;

      this.querySelector('[data-lightbox-open]')?.addEventListener('click', () => this.openLightbox(this.index));
      this.slides.forEach((slide, i) => slide.addEventListener('click', () => this.openLightbox(i)));
      this.lb.querySelector('[data-lightbox-close]').addEventListener('click', () => this.closeLightbox());
      this.lb.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.lbGo(this.lbIndex - 1));
      this.lb.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.lbGo(this.lbIndex + 1));
      this.lbThumbs.forEach((t, i) => t.addEventListener('click', () => this.lbGo(i)));

      this.lb.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeLightbox();
        if (e.key === 'ArrowRight') this.lbGo(this.lbIndex + 1);
        if (e.key === 'ArrowLeft') this.lbGo(this.lbIndex - 1);
      });

      let raf;
      this.lbTrack.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(this.lbTrack.scrollLeft / this.lbTrack.clientWidth);
          if (i !== this.lbIndex) this.lbMark(i);
        });
      }, { passive: true });

      // Zoom: clic para ampliar 2.2x en el punto tocado; mover el mouse recorre la foto
      this.lbSlides.forEach((slide) => {
        const img = slide.querySelector('img');
        const setOrigin = (e) => {
          const r = slide.getBoundingClientRect();
          img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
        };
        slide.addEventListener('click', (e) => {
          setOrigin(e);
          slide.classList.toggle('is-zoomed');
        });
        slide.addEventListener('mousemove', (e) => {
          if (slide.classList.contains('is-zoomed')) setOrigin(e);
        });
      });
    }

    loadLightboxImage(i) {
      [i - 1, i, i + 1].forEach((n) => {
        const img = this.lbSlides[(n + this.lbSlides.length) % this.lbSlides.length]?.querySelector('img');
        if (img && !img.src && img.dataset.src) img.src = img.dataset.src;
      });
    }

    openLightbox(i) {
      this.lbFocus = document.activeElement;
      this.lb.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        this.lbTrack.scrollLeft = i * this.lbTrack.clientWidth;
        this.lbMark(i);
        this.lb.querySelector('[data-lightbox-close]').focus();
      });
    }

    closeLightbox() {
      this.lbSlides.forEach((s) => s.classList.remove('is-zoomed'));
      this.lb.hidden = true;
      document.documentElement.style.overflow = '';
      this.go(this.lbIndex);
      if (this.lbFocus && document.contains(this.lbFocus)) this.lbFocus.focus();
    }

    lbGo(i) {
      const n = this.lbSlides.length;
      const next = (i + n) % n;
      this.lbSlides.forEach((s) => s.classList.remove('is-zoomed'));
      this.lbTrack.scrollTo({ left: next * this.lbTrack.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
      this.lbMark(next);
    }

    lbMark(i) {
      this.lbIndex = i;
      this.loadLightboxImage(i);
      if (this.lbCounter) this.lbCounter.textContent = `${i + 1} / ${this.lbSlides.length}`;
      this.lbThumbs.forEach((t, n) => {
        t.setAttribute('aria-pressed', n === i ? 'true' : 'false');
        if (n === i) t.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    }

    go(i) {
      const n = this.slides.length;
      const next = (i + n) % n;
      this.track.scrollTo({ left: next * this.track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
      this.mark(next);
    }

    goToMedia(mediaId) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i >= 0) this.go(i);
    }

    mark(i) {
      this.index = i;
      if (this.counter) {
        const pad = (n) => (this.counter.dataset.pad ? String(n).padStart(Number(this.counter.dataset.pad), '0') : String(n));
        this.counter.textContent = `${pad(i + 1)} / ${pad(this.slides.length)}`;
      }
      this.thumbs.forEach((t, n) => t.setAttribute('aria-pressed', n === i ? 'true' : 'false'));
      const thumb = this.thumbs[i];
      const r = this.thumbRail;
      if (thumb && r) {
        const behavior = reduceMotion ? 'auto' : 'smooth';
        if (r.scrollHeight > r.clientHeight + 2) {
          // Columna vertical (escritorio)
          const top = thumb.offsetTop - r.offsetTop;
          if (top < r.scrollTop || top + thumb.offsetHeight > r.scrollTop + r.clientHeight) r.scrollTo({ top: top - 8, behavior });
        } else {
          const left = thumb.offsetLeft - r.offsetLeft;
          if (left < r.scrollLeft || left + thumb.offsetWidth > r.scrollLeft + r.clientWidth) r.scrollTo({ left: left - 8, behavior });
        }
      }
    }

    updateThumbNav() {
      const r = this.thumbRail;
      if (!r) return;
      const max = r.scrollWidth - r.clientWidth;
      this.thumbWrap?.classList.toggle('has-overflow', max > 2);
      if (this.thumbPrev) this.thumbPrev.disabled = r.scrollLeft <= 2;
      if (this.thumbNext) this.thumbNext.disabled = r.scrollLeft >= max - 2;
    }
  }

  /* ===== Selector de variantes (tallas) + botón de compra + barra fija móvil ===== */
  class CProductForm extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('script[data-product-json]');
      if (!json) return;
      this.product = JSON.parse(json.textContent);
      this.form = this.querySelector('form[data-c-product-form]');
      this.idInput = this.form.querySelector('input[name="id"]');
      this.button = this.form.querySelector('[data-add-button]');
      this.warn = this.querySelector('[data-variant-warn]');
      this.money = this.dataset.moneyFormat || '${{amount_no_decimals_with_comma_separator}}';
      this.labels = { choose: this.dataset.labelChoose, add: this.dataset.labelAdd, soldout: this.dataset.labelSoldout };
      this.section = this.closest('.c-section');
      this.selected = this.product.options.map((_, i) => {
        const checked = this.querySelector(`input[data-option-index="${i}"]:checked`);
        return checked ? checked.value : null;
      });

      this.addEventListener('change', (e) => {
        if (!e.target.matches('input[data-option-index]')) return;
        this.selected[Number(e.target.dataset.optionIndex)] = e.target.value;
        if (this.builder && this.current()) this.advance(0);
        this.update(true);
      });

      // Paquetes (1, 2 o 3 camisas): cambian la cantidad y el precio mostrado
      this.qtyInput = this.form.querySelector('[data-quantity]');
      this.packs = [...this.querySelectorAll('[data-pack-qty]')];
      this.packs.forEach((p) =>
        p.addEventListener('change', () => {
          const n = this.packQty();
          const missing = this.missingSlot(n);
          this.active = missing >= 0 ? missing : Math.min(1, n - 1);
          this.update(false);
        })
      );

      // Arma tu paquete: la prenda 1 es este producto; la 2 y la 3 pueden ser otro, cada una con su talla
      this.builder = this.querySelector('[data-pack-builder]');
      this.itemsInput = this.form.querySelector('[data-pack-items]');
      if (this.builder && this.itemsInput) {
        this.packProducts = JSON.parse(this.builder.querySelector('[data-pack-products]').textContent);
        this.slotEls = [...this.builder.querySelectorAll('[data-slot]')];
        this.slots = this.slotEls.map(() => ({ pid: this.product.id, vid: null }));
        this.active = 0;
        this.packWarn = false;
        this.view = this.section?.querySelector('[data-pack-view]');
        this.strip = this.section?.querySelector('[data-pack-strip]');

        this.slotEls.forEach((el, i) => {
          el.querySelector('[data-slot-open]').addEventListener('click', () => {
            this.active = i;
            this.update(false);
          });
          el.querySelectorAll('[data-model]').forEach((btn) =>
            btn.addEventListener('click', () => this.pickModel(i, Number(btn.dataset.model)))
          );
          const sizes = el.querySelector('[data-slot-sizes]');
          if (sizes) {
            sizes.addEventListener('click', (e) => {
              const btn = e.target.closest('[data-variant]');
              if (!btn || btn.disabled) return;
              this.slots[i].vid = Number(btn.dataset.variant);
              this.advance(i);
              this.update(false);
            });
            this.renderSizes(i);
          }
        });

        this.strip?.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-strip]');
          if (!btn) return;
          this.active = Number(btn.dataset.strip);
          this.update(false);
        });

        // Paquete que crece (agregó el de 2 y luego elige el de 3): solo se agrega lo que falta.
        // Cuenta lo que este formulario ya agregó y sigue en el carrito.
        this.added = new Map();
        this.form.cAdjustItems = async (items) => {
          this.pending = items;
          if (!this.added.size) return items;
          try {
            const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
            const inCart = new Map();
            cart.items.forEach((i) => inCart.set(i.variant_id, (inCart.get(i.variant_id) || 0) + i.quantity));
            return items
              .map((it) => ({ id: it.id, quantity: it.quantity - Math.min(this.added.get(it.id) || 0, inCart.get(it.id) || 0) }))
              .filter((it) => it.quantity > 0);
          } catch (err) {
            return items;
          }
        };

        this.form.addEventListener('c:missing-pack', () => {
          this.packWarn = true;
          this.active = Math.max(0, this.missingSlot(this.packQty()));
          this.update(false);
          this.slotEls[this.active].scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      } else {
        this.builder = null;
      }

      this.form.addEventListener('c:missing-variant', () => {
        this.showWarn();
        if (this.builder) {
          this.packWarn = true;
          this.active = 0;
          this.update(false);
        }
        this.querySelector('[data-variant-picker]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });

      this.sticky = this.section && this.section.querySelector('[data-sticky-bar]');
      this.sticky?.querySelector('[data-sticky-add]')?.addEventListener('click', () => this.form.requestSubmit());

      // Después de agregar: "Agregado ✓" en el botón; c-cart abre el side cart unos segundos
      this.form.addEventListener('c:added', () => {
        if (this.builder && this.pending) this.added = new Map(this.pending.map((i) => [i.id, i.quantity]));
        const buttons = [this.button, this.sticky?.querySelector('[data-sticky-add]')].filter(Boolean);
        buttons.forEach((btn) => {
          btn.textContent = 'Agregado ✓';
          btn.classList.add('is-added');
        });
        setTimeout(() => {
          buttons.forEach((btn) => btn.classList.remove('is-added'));
          this.update(false);
        }, 2000);
      });
      this.update(false);
    }

    formatMoney(cents) {
      const value = (cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return this.money.replace(/\{\{\s*\w+\s*\}\}/, value);
    }

    current() {
      if (this.selected.some((v) => v === null)) return null;
      return this.product.variants.find((v) => v.options.every((o, i) => o === this.selected[i])) || null;
    }

    showWarn() {
      if (this.warn) this.warn.hidden = false;
      this.classList.add('is-warning');
    }

    // Mensaje de WhatsApp ordenado (mismo formato que snippets/c-whatsapp-product-message):
    // camisa(s), talla(s), precio y el enlace en su línea para que WhatsApp muestre la foto
    updateWhatsApp(variant, qty, price, full, total, off) {
      const link = this.querySelector('[data-wa-link]');
      if (!link) return;
      const d = link.dataset;
      // Enlace limpio y solo en su línea (sin ?variant): WhatsApp lo reconoce entero y saca la foto
      const url = d.waUrl;
      const hasSizes = this.product.variants.length > 1;
      const lines = [];
      if (qty > 1 && this.builder) {
        lines.push(`¡Hola NOAN! Quiero comprar este paquete:`, '', `*Paquete de ${qty} camisas${off ? ` (${off}% de descuento)` : ''}*`);
        for (let i = 0; i < qty; i++) {
          const p = this.packProduct(this.slots[i].pid);
          const v = (this.filled && this.filled[i]) || null;
          lines.push(`${i + 1}. ${p.title} - Talla ${v ? v.title : 'por definir'}`);
        }
        lines.push('', `*Total: ${this.formatMoney(total)}*`);
        if (full > total) lines.push(`Ahorras ${this.formatMoney(full - total)}`);
      } else {
        lines.push(d.waIntro || '¡Hola NOAN! Quiero comprar esta camisa:', '', `*${this.product.title}*`);
        if (hasSizes) lines.push(`Talla: ${variant ? variant.title : 'por definir'}`);
        lines.push(`Precio: ${this.formatMoney(price)}`);
      }
      lines.push('', url);
      if (d.waClosing) lines.push('', d.waClosing);
      link.href = `https://wa.me/${d.waNumber}?text=${encodeURIComponent(lines.join('\n'))}`;
    }

    packQty() {
      const pack = this.packs.find((p) => p.checked);
      return pack ? Number(pack.dataset.packQty) : 1;
    }

    packProduct(pid) {
      return this.packProducts.find((p) => p.id === pid) || this.packProducts[0];
    }

    // Variante elegida en cada prenda del paquete (la 1 sale de las tallas de arriba)
    slotVariant(i) {
      if (i === 0) {
        const v = this.current();
        return v && v.available ? v : null;
      }
      const slot = this.slots[i];
      return this.packProduct(slot.pid).variants.find((v) => v.id === slot.vid && v.available) || null;
    }

    missingSlot(n) {
      for (let i = 0; i < n; i++) if (!this.slotVariant(i)) return i;
      return -1;
    }

    // Después de elegir talla, abre la siguiente prenda que falte
    advance(from) {
      const next = this.missingSlot(this.packQty());
      this.active = next >= 0 ? next : from;
      this.packWarn = false;
    }

    // Cambia el modelo de una prenda; conserva la talla si el otro modelo la tiene
    pickModel(i, pid) {
      const slot = this.slots[i];
      const before = slot.vid && this.packProduct(slot.pid).variants.find((v) => v.id === slot.vid);
      slot.pid = pid;
      const same = before && this.packProduct(pid).variants.find((v) => v.title === before.title && v.available);
      slot.vid = same ? same.id : null;
      this.renderSizes(i);
      this.update(false);
    }

    renderSizes(i) {
      const box = this.slotEls[i].querySelector('[data-slot-sizes]');
      if (!box) return;
      box.replaceChildren(
        ...this.packProduct(this.slots[i].pid).variants.map((v) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'c-main-product__slot-size-btn';
          btn.setAttribute('role', 'radio');
          btn.dataset.variant = v.id;
          btn.textContent = v.title;
          btn.disabled = !v.available;
          if (!v.available) btn.setAttribute('aria-label', `${v.title}, agotada`);
          return btn;
        })
      );
    }

    renderBuilder(n, variant) {
      const filled = Array.from({ length: n }, (_, i) => this.slotVariant(i));
      const ready = filled.every(Boolean);
      const count = filled.filter(Boolean).length;
      this.active = Math.min(this.active, n - 1);

      this.builder.querySelector('[data-builder-title]').textContent = n > 1 ? 'Arma tu paquete' : 'Elige tu talla';
      const progress = this.builder.querySelector('[data-builder-progress]');
      progress.textContent = n > 1 ? `${count} de ${n} listas` : ready ? 'Lista' : 'Falta la talla';
      progress.classList.toggle('is-ready', ready);

      this.slotEls.forEach((el, i) => {
        const show = i < n;
        el.hidden = !show;
        if (!show) return;
        const on = i === this.active;
        const product = this.packProduct(this.slots[i].pid);
        const v = filled[i];
        const size = i === 0 ? variant && variant.title : v && v.title;
        el.classList.toggle('is-active', on);
        el.classList.toggle('is-done', Boolean(v));
        el.classList.toggle('is-missing', this.packWarn && !v && !on);
        el.querySelector('[data-slot-open]').setAttribute('aria-expanded', on ? 'true' : 'false');
        el.querySelector('[data-slot-panel]').hidden = !on;
        // El riel de modelos mide 0 mientras el panel está oculto: recalcula sus flechas al abrirlo
        if (on) el.querySelectorAll('c-rail-nav').forEach((nav) => nav.bind && nav.bind());
        el.querySelector('[data-slot-name]').textContent = product.title;
        el.querySelector('[data-slot-size]').textContent = v ? `Talla ${size}` : 'Falta elegir la talla';
        el.querySelector('[data-slot-action]').textContent = on ? '' : v ? 'Editar' : 'Elegir';
        const img = el.querySelector('[data-slot-img]');
        if (img && product.thumb) img.src = product.thumb;
        if (i === 0) return;
        el.querySelector('[data-slot-model]').textContent = product.title;
        el.querySelectorAll('[data-model]').forEach((btn) => {
          btn.setAttribute('aria-checked', Number(btn.dataset.model) === product.id ? 'true' : 'false');
        });
        el.querySelector('[data-slot-size-label]').textContent = v ? v.title : 'elige una';
        el.querySelectorAll('[data-variant]').forEach((btn) => {
          btn.setAttribute('aria-checked', v && Number(btn.dataset.variant) === v.id ? 'true' : 'false');
        });
      });

      // Fotos apiladas del paquete elegido
      const checked = this.packs.find((p) => p.checked);
      checked?.closest('label').querySelectorAll('[data-pack-thumbs] img').forEach((img, i) => {
        const thumb = this.packProduct(this.slots[i]?.pid ?? this.product.id).thumb;
        if (thumb) img.src = thumb;
      });

      // Galería: foto grande de la prenda abierta + miniaturas del paquete
      const activeProduct = this.packProduct(this.slots[this.active].pid);
      if (this.view) {
        this.view.hidden = n < 2 || !activeProduct.img;
        this.view.querySelector('[data-pack-view-img]').src = activeProduct.img || '';
        this.view.querySelector('[data-pack-view-img]').alt = activeProduct.title;
        this.view.querySelector('[data-pack-caption]').textContent = `Camisa ${this.active + 1} de ${n} · ${activeProduct.title}`;
      }
      if (this.strip) {
        this.strip.hidden = n < 2;
        this.strip.replaceChildren(
          ...(n < 2 ? [] : this.slots.slice(0, n)).map((slot, i) => {
            const p = this.packProduct(slot.pid);
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.dataset.strip = i;
            btn.setAttribute('aria-label', `Ver camisa ${i + 1}: ${p.title}`);
            btn.setAttribute('aria-pressed', i === this.active ? 'true' : 'false');
            const img = document.createElement('img');
            img.src = p.thumb || '';
            img.alt = '';
            btn.append(img);
            return btn;
          })
        );
      }
      return filled;
    }

    update(fromUser) {
      const variant = this.current();
      this.idInput.value = variant && variant.available ? variant.id : '';
      if (variant) {
        if (this.warn) this.warn.hidden = true;
        this.classList.remove('is-warning');
      }

      // Marca tallas agotadas según las demás opciones elegidas
      this.product.options.forEach((_, i) => {
        this.querySelectorAll(`input[data-option-index="${i}"]`).forEach((input) => {
          // ¿Hay alguna variante disponible con este valor y las demás opciones ya elegidas?
          const available = this.product.variants.some(
            (v) =>
              v.available &&
              v.options[i] === input.value &&
              v.options.every((o, n) => n === i || this.selected[n] === null || o === this.selected[n])
          );
          input.closest('label')?.classList.toggle('is-unavailable', !available);
        });
        const label = this.querySelector(`[data-option-value="${i}"]`);
        if (label) label.textContent = this.selected[i] || this.labels.choose.toLowerCase();
      });

      const price = variant ? variant.price : this.product.price;
      const compare = variant ? variant.compare_at_price : this.product.compare_at_price;
      const qty = this.packQty();
      const pack = this.packs.find((p) => p.checked);
      const off = pack ? Number(pack.dataset.packOff) || 0 : 0;
      let full = price * qty;
      let missing = -1;
      if (this.qtyInput) this.qtyInput.value = qty;
      if (this.builder) {
        const filled = this.renderBuilder(qty, variant);
        this.filled = filled;
        missing = filled.findIndex((v) => !v);
        full = filled.reduce((sum, v) => sum + (v ? v.price : price), 0);
        // Agrupa por variante: dos de la misma talla van como una línea con cantidad 2
        const items = [];
        filled.forEach((v) => {
          if (!v) return;
          const line = items.find((i) => i.id === v.id);
          if (line) line.quantity += 1;
          else items.push({ id: v.id, quantity: 1 });
        });
        this.itemsInput.disabled = qty < 2;
        this.itemsInput.value = missing < 0 ? JSON.stringify(items) : '';
      }
      const total = Math.round((full * (100 - off)) / 100);

      this.packs.forEach((p) => {
        const n = Number(p.dataset.packQty);
        const o = Number(p.dataset.packOff) || 0;
        const base = p.checked ? full : price * n;
        const box = p.closest('label');
        const priceEl = box && box.querySelector('[data-pack-price]');
        const compareEl = box && box.querySelector('[data-pack-compare]');
        if (priceEl) priceEl.textContent = this.formatMoney(Math.round((base * (100 - o)) / 100));
        if (compareEl) compareEl.textContent = this.formatMoney(base);
      });
      if (this.builder) {
        this.builder.querySelector('[data-builder-total]').hidden = qty < 2;
        this.builder.querySelector('[data-builder-amount]').textContent = this.formatMoney(total);
        this.builder.querySelector('[data-builder-saving]').textContent = full > total ? `Ahorras ${this.formatMoney(full - total)}` : '';
      }
      // Precio de arriba: en un paquete muestra el total del paquete
      const shown = qty > 1 ? total : price;
      const shownCompare = qty > 1 ? (full > total ? full : 0) : compare && compare > price ? compare : 0;
      this.section?.querySelectorAll('[data-price]').forEach((el) => (el.textContent = this.formatMoney(shown)));
      this.section?.querySelectorAll('[data-compare-price]').forEach((el) => {
        el.textContent = shownCompare ? this.formatMoney(shownCompare) : '';
      });
      this.updateWhatsApp(variant, qty, price, full, total, off);

      let text = this.labels.choose;
      let state = 'choose';
      if (variant && !variant.available) {
        text = this.labels.soldout;
        state = 'soldout';
      } else if (qty > 1 && this.builder) {
        text = missing < 0 ? `Agregar paquete · ${this.formatMoney(total)}` : `Elige la talla de la camisa ${missing + 1}`;
        state = missing < 0 ? 'ready' : 'choose';
      } else if (variant) {
        text = `${this.labels.add} · ${this.formatMoney(total)}`;
        state = 'ready';
      }
      [this.button, this.sticky?.querySelector('[data-sticky-add]')].forEach((btn) => {
        if (!btn) return;
        btn.textContent = text;
        btn.dataset.state = state;
        btn.disabled = state === 'soldout';
      });

      if (fromUser && variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
        if (variant.featured_media) this.section?.querySelector('c-gallery')?.goToMedia(variant.featured_media.id);
      }
    }
  }

  /* ===== Buscador emergente con resultados en vivo ===== */
  class CSearchModal extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.form = this.querySelector('[data-search-form]');
      this.suggest = this.querySelector('[data-search-suggest]');
      this.results = this.querySelector('[data-search-results]');
      this.list = this.querySelector('[data-search-list]');
      this.status = this.querySelector('[data-search-status]');
      this.empty = this.querySelector('[data-search-empty]');
      this.echo = this.querySelector('[data-search-echo]');
      this.all = this.querySelector('[data-search-all]');
      this.money = this.dataset.moneyFormat || '${{amount_no_decimals_with_comma_separator}}';
      this.timer = null;
      this.controller = null;

      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-search-open]');
        if (!opener) return;
        e.preventDefault();
        this.open(opener);
      });
      this.querySelector('[data-search-close]').addEventListener('click', () => this.close());
      this.addEventListener('click', (e) => { if (e.target === this) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(this.input.value.trim()), 250);
      });
      this.querySelectorAll('[data-search-term]').forEach((btn) =>
        btn.addEventListener('click', () => {
          this.input.value = btn.dataset.searchTerm;
          this.search(this.input.value);
          this.input.focus();
        })
      );
      this.all.addEventListener('click', () => this.form.submit());
    }

    open(opener) {
      this.lastFocus = opener || document.activeElement;
      this.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      setTimeout(() => this.input.focus(), 30);
    }

    close() {
      this.hidden = true;
      document.documentElement.style.overflow = '';
      if (this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus();
    }

    formatMoney(value) {
      const cents = typeof value === 'string' && value.includes('.') ? Math.round(parseFloat(value) * 100) : Number(value);
      const amount = (cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return this.money.replace(/\{\{\s*\w+\s*\}\}/, amount);
    }

    async search(q) {
      if (this.controller) this.controller.abort();
      if (!q) {
        this.suggest.hidden = false;
        this.results.hidden = true;
        return;
      }
      this.suggest.hidden = true;
      this.results.hidden = false;
      this.status.textContent = 'Buscando…';
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q,
        'resources[type]': 'product',
        'resources[limit]': '8',
        'resources[options][unavailable_products]': 'last',
      });
      try {
        const res = await fetch(`${this.dataset.suggestUrl}.json?${params}`, { signal: this.controller.signal });
        const data = await res.json();
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        this.render(q, products);
      } catch (err) {
        if (err.name !== 'AbortError') this.status.textContent = 'No pudimos buscar. Presiona Enter para ver los resultados.';
      }
    }

    render(q, products) {
      this.list.replaceChildren();
      this.echo.textContent = q;
      this.empty.hidden = products.length > 0;
      this.all.hidden = products.length === 0;
      this.status.textContent = products.length ? `${products.length} resultado${products.length === 1 ? '' : 's'} para “${q}”` : '';
      products.forEach((p) => {
        const a = document.createElement('a');
        a.className = 'c-search-modal__item';
        a.href = p.url;
        const media = document.createElement('span');
        media.className = 'c-search-modal__item-media';
        const src = (p.featured_image && p.featured_image.url) || p.image;
        if (src) {
          const img = document.createElement('img');
          img.src = `${src}${src.includes('?') ? '&' : '?'}width=160`;
          img.alt = '';
          img.loading = 'lazy';
          media.appendChild(img);
        }
        const info = document.createElement('span');
        info.className = 'c-search-modal__item-info';
        const title = document.createElement('span');
        title.className = 'c-search-modal__item-title';
        title.textContent = p.title;
        const price = document.createElement('span');
        price.className = 'c-search-modal__item-price';
        price.textContent = p.available === false ? 'Agotada' : this.formatMoney(p.price);
        info.append(title, price);
        a.append(media, info);
        this.list.appendChild(a);
      });
    }
  }

  /* ===== "X personas están viendo ahora": cada producto arranca en su número y oscila ±3 ===== */
  const startViewers = () => {
    document.querySelectorAll('[data-viewers-line]').forEach((line) => {
      const out = line.querySelector('[data-viewers]');
      const min = Number(line.dataset.min) || 0;
      const max = Math.max(min, Number(line.dataset.max) || 0);
      if (!out || max === 0) return;
      const start = Math.min(max, Math.max(min, Number(line.dataset.start) || min));
      const low = Math.max(min, start - 3);
      const high = Math.min(max, start + 3);
      let value = start;
      const tick = () => {
        const step = Math.random() < 0.5 ? -1 : 1;
        value = Math.min(high, Math.max(low, value + step * (1 + Math.round(Math.random()))));
        out.style.opacity = '0';
        setTimeout(() => { out.textContent = value; out.style.opacity = '1'; }, 300);
        setTimeout(tick, 5000 + Math.random() * 4000);
      };
      setTimeout(tick, 5000 + Math.random() * 4000);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startViewers);
  else startViewers();

  /* ===== "+ Agregar" de las tarjetas: talla → Agregar al carrito → "Agregado" y se cierra ===== */
  document.addEventListener('click', (e) => {
    const size = e.target.closest('[data-quick-size]');
    if (size) {
      const picker = size.closest('[data-quick-picker]');
      picker.querySelectorAll('[data-quick-size]').forEach((b) => b.setAttribute('aria-pressed', b === size ? 'true' : 'false'));
      picker.querySelector('input[name="id"]').value = size.dataset.quickSize;
      const confirm = picker.querySelector('[data-quick-confirm]');
      confirm.hidden = false;
      confirm.textContent = `Agregar talla ${size.textContent.trim()}`;
      return;
    }
    // Clic fuera: cierra los selectores de talla abiertos
    document.querySelectorAll('[data-quick-picker][open]').forEach((d) => {
      if (!d.contains(e.target)) d.open = false;
    });
  });

  const resetPicker = (picker) => {
    picker.open = false;
    picker.querySelectorAll('[data-quick-size]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
    picker.querySelector('input[name="id"]').value = '';
    const confirm = picker.querySelector('[data-quick-confirm]');
    confirm.hidden = true;
    confirm.classList.remove('is-added');
    confirm.disabled = false;
    const error = picker.querySelector('[data-form-error]');
    if (error) error.hidden = true;
  };

  document.addEventListener('c:added', (e) => {
    const form = e.target.closest('[data-quick-add]');
    if (!form) return;
    const confirm = form.querySelector('[data-quick-confirm]');
    const picker = form.closest('[data-quick-picker]');
    const before = confirm.innerHTML;
    confirm.classList.add('is-added');
    confirm.textContent = 'Agregado ✓';
    // setBusy de c-cart lo reactiva al terminar; lo dejamos quieto mientras se ve "Agregado".
    // A los 900 ms c-cart abre el side cart; el selector se cierra detrás.
    setTimeout(() => (confirm.disabled = true), 0);
    setTimeout(() => {
      if (picker) resetPicker(picker);
      else {
        confirm.classList.remove('is-added');
        confirm.innerHTML = before;
        confirm.disabled = false;
      }
    }, 1300);
  });

  document.addEventListener('toggle', (e) => {
    if (e.target.matches?.('[data-quick-picker]') && !e.target.open) {
      // Si lo cierran a mano sin agregar, vuelve a empezar
      if (!e.target.querySelector('.is-added')) resetPicker(e.target);
    }
  }, true);

  if (!customElements.get('c-search-modal')) customElements.define('c-search-modal', CSearchModal);
  if (!customElements.get('c-gallery')) customElements.define('c-gallery', CGallery);
  if (!customElements.get('c-product-form')) customElements.define('c-product-form', CProductForm);

  if (!customElements.get('c-rail-nav')) customElements.define('c-rail-nav', CRailNav);
  if (!customElements.get('c-collections-showcase')) customElements.define('c-collections-showcase', CCollectionsShowcase);
  if (!customElements.get('c-tabs')) customElements.define('c-tabs', CTabs);

  /* ===== Bandas que visten NOAN: una tarjeta abierta, avanza sola y se pausa al usarla ===== */
  class CBandGallery extends HTMLElement {
    connectedCallback() {
      this.cards = [...this.querySelectorAll('[data-band]')];
      this.bars = [...this.querySelectorAll('[data-band-bar]')];
      this.num = this.querySelector('[data-band-num]');
      this.track = this.querySelector('[data-band-track]');
      this.index = 0;
      this.delay = Number(this.dataset.interval) || 4500;
      this.paused = false;
      this.visible = false;
      if (!this.cards.length) return;

      // Clic en una cerrada la abre; en la abierta (o en la lupa) amplía la foto
      this.cards.forEach((card, i) => {
        card.querySelector('[data-band-pick]').addEventListener('click', () => {
          if (i === this.index && card.dataset.full) this.openLb(i);
          else this.go(i, true);
        });
        card.querySelector('[data-band-zoom]')?.addEventListener('click', () => this.openLb(i));
      });
      this.setupLb();
      this.querySelector('[data-band-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-band-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.addEventListener('pointerenter', () => (this.paused = true));
      this.addEventListener('pointerleave', () => (this.paused = false));
      this.addEventListener('focusin', () => (this.paused = true));
      this.addEventListener('focusout', () => (this.paused = false));

      // Solo avanza cuando se ve en pantalla y si el sistema no pide menos movimiento
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!reduced && this.cards.length > 1) {
        this.io = new IntersectionObserver((entries) => (this.visible = entries[0].isIntersecting), { threshold: 0.3 });
        this.io.observe(this);
        this.timer = setInterval(() => {
          if (this.visible && !this.paused && !this.lb?.open && !document.hidden) this.go(this.index + 1, false);
        }, this.delay);
      }
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.io?.disconnect();
    }

    /* ----- Foto ampliada: solo las tarjetas con foto; clic/toque acerca y el puntero recorre ----- */
    setupLb() {
      this.lb = this.querySelector('[data-band-lb]');
      this.photos = this.cards.map((c, i) => ({ i, src: c.dataset.full })).filter((p) => p.src);
      if (!this.lb || !this.photos.length) return;
      this.lbStage = this.lb.querySelector('[data-band-lb-stage]');
      this.lbImg = this.lb.querySelector('[data-band-lb-img]');
      this.lbCount = this.lb.querySelector('[data-band-lb-count]');
      const prev = this.lb.querySelector('[data-band-lb-prev]');
      const next = this.lb.querySelector('[data-band-lb-next]');
      prev.hidden = next.hidden = this.photos.length < 2;
      prev.addEventListener('click', () => this.lbShow(this.lbPos - 1));
      next.addEventListener('click', () => this.lbShow(this.lbPos + 1));
      this.lb.querySelector('[data-band-lb-close]').addEventListener('click', () => this.lb.close());
      this.lb.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.lbShow(this.lbPos + 1);
        if (e.key === 'ArrowLeft') this.lbShow(this.lbPos - 1);
      });
      this.lb.addEventListener('close', () => {
        this.lbStage.classList.remove('is-zoomed');
        document.documentElement.style.overflow = '';
        // Al cerrar, la galería queda en la foto que se estaba viendo
        const card = this.photos[this.lbPos]?.i;
        if (card !== undefined && card !== this.index) this.go(card, true);
      });

      // El punto de acercamiento se mide sobre el marco (no cambia con el zoom)
      const setOrigin = (e) => {
        const box = this.lbStage.getBoundingClientRect();
        const x = Math.min(Math.max((e.clientX - box.left) / box.width, 0), 1);
        const y = Math.min(Math.max((e.clientY - box.top) / box.height, 0), 1);
        this.lbImg.style.transformOrigin = `${x * 100}% ${y * 100}%`;
      };
      this.lbStage.addEventListener('click', (e) => {
        setOrigin(e);
        this.lbStage.classList.toggle('is-zoomed');
      });
      this.lbStage.addEventListener('pointermove', (e) => {
        if (this.lbStage.classList.contains('is-zoomed')) setOrigin(e);
      });
    }

    openLb(cardIndex) {
      if (!this.lb || !this.photos.length) return;
      const pos = this.photos.findIndex((p) => p.i === cardIndex);
      this.lbShow(pos < 0 ? 0 : pos);
      document.documentElement.style.overflow = 'hidden';
      this.lb.showModal();
    }

    lbShow(pos) {
      const n = this.photos.length;
      this.lbPos = (pos + n) % n;
      this.lbStage.classList.remove('is-zoomed');
      this.lbImg.src = this.photos[this.lbPos].src;
      if (this.lbCount) this.lbCount.textContent = `${this.lbPos + 1} / ${n}`;
    }

    go(i, fromUser) {
      const n = this.cards.length;
      this.index = (i + n) % n;
      this.cards.forEach((card, k) => {
        const on = k === this.index;
        card.classList.toggle('is-open', on);
        card.querySelector('[data-band-pick]').setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.bars.forEach((bar, k) => bar.classList.toggle('is-on', k === this.index));
      if (this.num) this.num.textContent = String(this.index + 1).padStart(2, '0');
      // En móvil la fila se desliza: lleva la tarjeta abierta a la vista
      // (espera a que termine la animación de ancho para medir bien)
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        if (!this.track || this.track.scrollWidth <= this.track.clientWidth) return;
        const card = this.cards[this.index];
        const left = card.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
        this.track.scrollTo({ left, behavior: 'smooth' });
      }, 650);
      if (fromUser && this.timer) {
        // Reinicia la cuenta para que no salte justo después de un clic
        clearInterval(this.timer);
        this.timer = setInterval(() => {
          if (this.visible && !this.paused && !this.lb?.open && !document.hidden) this.go(this.index + 1, false);
        }, this.delay);
      }
    }
  }
  if (!customElements.get('c-band-gallery')) customElements.define('c-band-gallery', CBandGallery);

  /* ===== Así la llevan ellos: ventana con la camisa de cada foto de cliente ===== */
  class CUgc extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('[data-ugc-modal]');
      // Solo las fotos "reales" (no la copia que hace girar la fila)
      this.tiles = [...this.querySelectorAll('[data-ugc-set] [data-ugc-tile]')];
      if (!this.modal || !this.tiles.length) return;
      this.tiles.forEach((tile, i) => tile.addEventListener('click', () => this.open(i, tile)));
      this.querySelector('[data-ugc-close]').addEventListener('click', () => this.modal.close());
      this.querySelector('[data-ugc-prev]').addEventListener('click', () => this.show(this.index - 1));
      this.querySelector('[data-ugc-next]').addEventListener('click', () => this.show(this.index + 1));
      // Clic en el fondo oscuro cierra
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) this.modal.close();
      });
      this.modal.addEventListener('close', () => {
        this.classList.remove('is-paused');
        this.lastTile?.focus({ preventScroll: true });
      });
    }

    open(i, tile) {
      this.lastTile = tile;
      this.show(i);
      this.classList.add('is-paused');
      this.modal.showModal();
    }

    show(i) {
      const n = this.tiles.length;
      this.index = (i + n) % n;
      const d = this.tiles[this.index].dataset;
      const img = this.modal.querySelector('[data-ugc-img]');
      img.src = d.img || '';
      img.alt = [d.handle, d.item].filter(Boolean).join(' con la ');
      img.hidden = !d.img;
      const who = this.modal.querySelector('[data-ugc-who]');
      who.textContent = d.handle ? `${d.handle} lleva` : 'Lleva';
      this.modal.querySelector('[data-ugc-name]').textContent = d.item;
      this.modal.querySelector('[data-ugc-price]').textContent = d.price || '';
      const link = this.modal.querySelector('[data-ugc-link]');
      link.href = d.url || '#';
      link.hidden = !d.url;
      // Sin producto: solo la foto grande
      this.modal.querySelector('[data-ugc-card]')?.classList.toggle('is-photo-only', !d.url);
    }
  }
  if (!customElements.get('c-ugc')) customElements.define('c-ugc', CUgc);

  /* ===== Hero: con más de una diapositiva, slider con flechas, puntos, deslizar y avance solo ===== */
  class CHeroSlider extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-hero-slide]')];
      this.dots = [...this.querySelectorAll('[data-hero-dot]')];
      this.index = 0;
      if (this.slides.length < 2) return;

      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
      this.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-hero-next]')?.addEventListener('click', () => this.go(this.index + 1));

      // Deslizar en pantallas táctiles
      this.addEventListener('touchstart', (e) => (this.x0 = e.touches[0].clientX), { passive: true });
      this.addEventListener('touchend', (e) => {
        if (this.x0 == null) return;
        const dx = e.changedTouches[0].clientX - this.x0;
        this.x0 = null;
        if (Math.abs(dx) > 50) this.go(this.index + (dx < 0 ? 1 : -1));
      });

      // En el editor: seleccionar una diapositiva la muestra y detiene el avance
      this.onSelect = (e) => {
        const i = this.slides.indexOf(e.target.closest?.('[data-hero-slide]'));
        if (i < 0) return;
        this.editing = true;
        this.go(i);
      };
      this.onDeselect = () => (this.editing = false);
      document.addEventListener('shopify:block:select', this.onSelect);
      document.addEventListener('shopify:block:deselect', this.onDeselect);

      if (this.hasAttribute('data-autoplay') && !reduceMotion) {
        this.delay = Number(this.dataset.interval) || 6000;
        this.paused = false;
        this.visible = true;
        this.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && (this.paused = true));
        this.addEventListener('pointerleave', () => (this.paused = false));
        this.addEventListener('focusin', () => (this.paused = true));
        this.addEventListener('focusout', () => (this.paused = false));
        this.io = new IntersectionObserver((entries) => (this.visible = entries[0].isIntersecting), { threshold: 0.3 });
        this.io.observe(this);
        this.restart();
      }
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.io?.disconnect();
      document.removeEventListener('shopify:block:select', this.onSelect);
      document.removeEventListener('shopify:block:deselect', this.onDeselect);
    }

    restart() {
      if (!this.delay) return;
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        if (this.visible && !this.paused && !this.editing && !document.hidden) this.go(this.index + 1, false);
      }, this.delay);
    }

    go(i, user = true) {
      const n = this.slides.length;
      this.index = (i + n) % n;
      this.slides.forEach((slide, k) => {
        const on = k === this.index;
        slide.classList.toggle('is-active', on);
        slide.toggleAttribute('inert', !on);
        if (on) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      this.dots.forEach((dot, k) => dot.setAttribute('aria-current', String(k === this.index)));
      // Si la persona cambió de diapositiva, el conteo vuelve a empezar
      if (user) this.restart();
    }
  }
  if (!customElements.get('c-hero-slider')) customElements.define('c-hero-slider', CHeroSlider);

  /* ===== Reseñas del producto: "Escribir una reseña" abre el formulario de Judge.me =====
     El widget va oculto (sus reseñas se pintan en las tarjetas); al pedir escribir se muestra
     solo su formulario. Si Judge.me no cargó, el enlace sigue a WhatsApp. */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-review-write]');
    if (!btn) return;
    const source = btn.closest('section')?.querySelector('[data-review-source]');
    const link = source?.querySelector('.jdgm-write-rev-link');
    if (!link) return;
    e.preventDefault();
    source.classList.add('is-writing');
    link.click();
    requestAnimationFrame(() => source.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' }));
  });
})();
